import { forwardRef, useRef, useState, ReactNode, RefObject } from "react";
import { Asterisk, DollarSign, Eye, EyeOff, Search, X } from "lucide-react";
import { cn } from "@/lib/utils"; // Añade esta importación al inicio
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface BaseProps {
  children: ReactNode;
  className?: string;
}

// Simplificar las interfaces eliminando props de estilo
interface InputContainerProps extends BaseProps {
  rounded?: boolean;
}

type InputSingleContainerProps = BaseProps;

interface InputIconProps extends BaseProps {
  onClick?: () => void;
  inputIconClassname?: string;
}

interface LabelProps extends BaseProps {
  labelClassname?: string;
  id: string | undefined;
  name?: string;
  isRequired?: boolean;
}

interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  rounded?: boolean;
  inputType?: string;
  icon?: ReactNode;
  isInvalid?: boolean;
  endAdorment?: ReactNode;
  hideClearButton?: boolean;
  inputClassname?: string;
  clearInput?: () => void;
  inputMode?:
    | "none"
    | "text"
    | "decimal"
    | "numeric"
    | "tel"
    | "search"
    | "email"
    | "url";
}

interface InputClearIconProps extends BaseProps {
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  inputClearIconClassname?: string;
}

interface ErrorMessageProps {
  children: ReactNode;
  errorMessageClassname?: string;
}

interface HelperTextProps {
  children: ReactNode;
  helperTextClassname?: string;
}

interface EndAdormentContainerProps extends BaseProps {
  onClick?: () => void;
  endAdormentClassname?: string;
}

interface InputProps
  extends Omit<BaseProps, "children">,
    React.InputHTMLAttributes<HTMLInputElement> {
  inputType?: "search" | "currency" | "password" | string;
  icon?: ReactNode;
  isInvalid?: boolean;
  endAdorment?: ReactNode;
  hideClearButton?: boolean;
  clearInput?: () => void;
  label?: string;
  helperText?: string;
  error?: string[];
  isRequired?: boolean;
  rounded?: boolean;
  labelClassname?: string;
  inputIconClassname?: string;
  endAdormentClassname?: string;
  inputClearIconClassname?: string;
  inputClassname?: string;
  helperTextClassname?: string;
}

export const InputContainer: React.FC<InputContainerProps> = ({
  children,
  className,
  rounded,
  ...props
}) => (
  <div
    className={cn(
      "w-full",
      "flex flex-col items-start justify-center gap-1",
      rounded ? "rounded-[20px]" : "rounded-[10px]",
      "bg-transparent",
      className
    )}
    {...props}
  >
    {children}
  </div>
);

export const InputSingleContainer: React.FC<InputSingleContainerProps> = ({
  children,
  className,
}) => <div className={`relative w-full ${className}`}>{children}</div>;

export const InputAndErrorContainer: React.FC<{ children: ReactNode }> = ({
  children,
}) => <div className="w-full">{children}</div>;

export const InputIcon: React.FC<InputIconProps> = ({
  children,
  inputIconClassname,
  ...props
}) => (
  <div
    className={`
      absolute top-1/2 left-0 transform translate-x-1/2 -translate-y-1/2 z-10
      text-xl transition-all duration-300
      ${inputIconClassname}
    `}
    {...props}
  >
    {children}
  </div>
);

export const Label: React.FC<LabelProps> = ({
  children,
  labelClassname,
  id,
  isRequired,
}) => (
  <label
    className={`
      m-0 transition-all duration-300
      flex items-center gap-1 select-none
      ${labelClassname}
    `}
    htmlFor={id}
  >
    {children}

    {isRequired && <IsRequired />}
  </label>
);

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      rounded,
      inputType,
      icon,
      isInvalid,
      type,
      endAdorment,
      clearInput,
      disabled,
      inputClassname,
      inputMode,
      ...props
    },
    ref
  ) => (
    <input
      className={cn(
        "w-full",
        "text-sm",
        rounded ? "rounded-[20px]" : "rounded-[10px]",
        "max-w-full p-2 px-3",
        "bg-background-100 text-text-primary",
        isInvalid
          ? "border-error-primary border shadow-error"
          : "border-border border",
        (inputType || icon) && "pl-10",
        "transition-all duration-300",
        disabled &&
          "bg-input-bg-disabled text-text-secondary cursor-not-allowed",
        "focus:outline-hidden focus:ring-1 focus:ring-brand-primary",
        isInvalid && "focus:ring-error-primary",
        "placeholder:text-text-secondary",
        endAdorment ? "pr-16" : clearInput ? "pr-9" : "pr-2.5",
        "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
        inputClassname
      )}
      type={type}
      inputMode={inputMode}
      ref={ref}
      value={props.value}
      onChange={props.onChange}
      onBlur={props.onBlur}
      onFocus={props.onFocus}
      onKeyDown={props.onKeyDown}
      // Atributos de input
      name={props.name}
      id={props.id}
      placeholder={props.placeholder}
      maxLength={props.maxLength}
      minLength={props.minLength}
      // Numéricos: step/min/max son indispensables para `type="number"`. La
      // interface ya los tipa via `React.InputHTMLAttributes`, faltaba sólo
      // el forward al `<input>` real.
      step={props.step}
      min={props.min}
      max={props.max}
      required={props.required}
      // Estados
      disabled={disabled}
      readOnly={props.readOnly}
      autoComplete={props.autoComplete}
      autoFocus={props.autoFocus}
      // Atributos de accesibilidad
      aria-label={props["aria-label"]}
      aria-required={props["aria-required"]}
      aria-invalid={props["aria-invalid"]}
    />
  )
);

InputField.displayName = "InputField";

export const InputClearIcon: React.FC<InputClearIconProps> = ({
  children,
  inputClearIconClassname,
  onClick,
}) => (
  <div
    className={`
      cursor-pointer rounded-full transition-all duration-300 p-1.5
      hover:bg-bg-2 hover:text-text-primary
      ${inputClearIconClassname}
    `}
    onClick={onClick}
  >
    {children}
  </div>
);

export const ErrorMessage: React.FC<ErrorMessageProps> = ({
  children,
  errorMessageClassname,
}) => (
  <p
    className={`m-0 text-alert-error-foreground bg-alert-error-background p-1 px-2.5 rounded w-fit ${errorMessageClassname}`}
  >
    {children}
  </p>
);

export const HelperText: React.FC<HelperTextProps> = ({
  children,
  helperTextClassname,
}) => (
  <div
    className={`text-text-secondary text-sm pl-1.5 font-normal ${helperTextClassname}`}
  >
    {children}
  </div>
);

export const IsRequired: React.FC<{ children?: ReactNode }> = ({
  children,
}) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger tabIndex={-1} type="button" aria-hidden="true" asChild>
        <div className="cursor-default rounded-full bg-error-light border text-error-foreground overflow-hidden transition-all duration-300 relative flex items-center">
          {children ? children : <Asterisk className="w-3 h-3" />}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>Requerido</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const EndIconsContainer: React.FC<{ children: ReactNode }> = ({
  children,
}) => (
  <div className="absolute top-0 right-4 flex items-center gap-1.5 h-full">
    {children}
  </div>
);

export const EndAdormentContainer: React.FC<EndAdormentContainerProps> = ({
  children,
  endAdormentClassname,
}) => <div className={`${endAdormentClassname}`}>{children}</div>;

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      inputType,
      icon,
      isInvalid,
      label,
      helperText,
      error,
      clearInput,
      rounded,

      labelClassname,
      inputIconClassname,
      endAdormentClassname,
      inputClearIconClassname,
      inputClassname,
      helperTextClassname,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState<string>("");
    const [currentInputType, setCurrentInputType] = useState<string>(
      props.type || "text"
    );
    const innerInputRef = useRef<HTMLInputElement>(null);
    const inputRef = (ref || innerInputRef) as RefObject<HTMLInputElement>;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      const newValue = e.target.value;
      if (props.onChange) {
        props.onChange(e);
      }
      setInputValue(newValue);
    };

    // Un buscador filtra mientras se escribe: el Enter no tiene nada que
    // confirmar. Pero adentro de un <form> con botón de guardar, el navegador
    // lo toma como "enviar" y guarda el formulario entero — en "Editar plan
    // mayorista" buscar un producto y apretar Enter guardaba el plan a medias.
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
      props.onKeyDown?.(e);
      if (
        inputType === "search" &&
        e.key === "Enter" &&
        !e.nativeEvent.isComposing
      ) {
        e.preventDefault();
      }
    };

    const handleClear = (e: React.MouseEvent<HTMLDivElement>): void => {
      e.preventDefault();
      e.stopPropagation();

      if (clearInput) {
        clearInput();
      }
      setInputValue("");
      inputRef.current?.focus();
    };

    const togglePasswordVisibility = (): void => {
      setCurrentInputType(
        currentInputType === "password" ? "text" : "password"
      );
    };

    return (
      <InputContainer className={className} rounded={rounded}>
        {label && (
          <Label
            labelClassname={labelClassname}
            id={props.id}
            isRequired={props.isRequired}
          >
            {label}
          </Label>
        )}

        <div className="flex flex-col gap-2 w-full">
          <InputAndErrorContainer>
            <InputSingleContainer>
              {/* Search/Currency icons */}
              {(inputType === "search" || inputType === "currency") && (
                <InputIcon
                  inputIconClassname={inputIconClassname}
                  onClick={() => inputRef.current?.focus()}
                >
                  {inputType === "search" ? (
                    <Search className="w-5 h-5" />
                  ) : (
                    <DollarSign />
                  )}
                </InputIcon>
              )}

              {/* Custom icon */}
              {icon && (
                <InputIcon
                  inputIconClassname={inputIconClassname}
                  onClick={() => inputRef.current?.focus()}
                >
                  {icon}
                </InputIcon>
              )}

              <InputField
                {...props}
                ref={inputRef}
                rounded={rounded}
                inputType={inputType}
                icon={icon}
                isInvalid={isInvalid}
                clearInput={clearInput}
                type={currentInputType}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                value={props.value !== undefined ? props.value : inputValue}
                inputClassname={inputClassname}
              />

              <EndIconsContainer>
                {props.endAdorment && (
                  <EndAdormentContainer
                    endAdormentClassname={endAdormentClassname}
                  >
                    {props.endAdorment}
                  </EndAdormentContainer>
                )}

                {inputType === "password" && (
                  <EndAdormentContainer
                    endAdormentClassname={endAdormentClassname}
                    onClick={togglePasswordVisibility}
                  >
                    {currentInputType === "password" ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </EndAdormentContainer>
                )}

                {clearInput && (props.value || inputValue) && (
                  <InputClearIcon
                    inputClearIconClassname={inputClearIconClassname}
                    onClick={handleClear}
                  >
                    <X className="w-5 h-5" />
                  </InputClearIcon>
                )}
              </EndIconsContainer>
            </InputSingleContainer>
          </InputAndErrorContainer>

          {helperText && (
            <HelperText helperTextClassname={helperTextClassname}>
              {helperText}
            </HelperText>
          )}

          {isInvalid && error && (
            <div className="flex flex-col gap-1">
              {error.map(
                (err, index) =>
                  err && <ErrorMessage key={index}>{err}</ErrorMessage>
              )}
            </div>
          )}
        </div>
      </InputContainer>
    );
  }
);

Input.displayName = "Input";

export default Input;
